import React, { useState, useEffect, useCallback } from 'react';
import { fetchConveyancingDashboard } from '../api';
import { formatCurrency } from '../utils/currency';

const CARD_STYLES = {
  lodgements: 'border-blue-200 bg-blue-50',
  prep: 'border-red-200 bg-red-50',
  burs: 'border-yellow-200 bg-yellow-50',
  compliance: 'border-amber-200 bg-amber-50',
  fia: 'border-orange-200 bg-orange-50',
  trust: 'border-green-200 bg-green-50',
  registrations: 'border-purple-200 bg-purple-50',
};

function DaysInStageBadge({ updatedAt }) {
  if (!updatedAt) return null;
  const days = Math.floor((Date.now() - new Date(updatedAt)) / 86400000);
  let cls = 'bg-gray-100 text-gray-600';
  if (days > 14) cls = 'bg-red-100 text-red-700';
  else if (days >= 7) cls = 'bg-amber-100 text-amber-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {days}d in stage
    </span>
  );
}

function StatusBadge({ value, label, color }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label ?? value}
    </span>
  );
}

function DashboardCard({ title, icon, color, children, count }) {
  return (
    <div className={`rounded-xl border-2 ${color} p-5 flex flex-col gap-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{icon}</span>
          <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
        </div>
        {count !== undefined && (
          <span className="text-2xl font-bold text-gray-700">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function CaseLink({ setActiveTab, caseId, children }) {
  return (
    <button
      onClick={() => setActiveTab && setActiveTab('cases')}
      className="text-blue-600 hover:underline text-left"
    >
      {children}
    </button>
  );
}

export default function ConveyancingDashboard({ authAxios, showFormMessage, setActiveTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchConveyancingDashboard(authAxios);
      setData(d);
    } catch {
      showFormMessage?.('error', 'Failed to load conveyancing dashboard.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <svg className="animate-spin h-8 w-8 mr-3" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Conveyancing Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Live overview of all conveyancing matters</p>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
        >
          ↻ Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

        {/* 1. Today's Lodgements */}
        <DashboardCard
          title="Today's Lodgements"
          icon="🏛️"
          color={CARD_STYLES.lodgements}
          count={data.todays_lodgements?.length ?? 0}
        >
          {data.todays_lodgements?.length === 0 && (
            <p className="text-sm text-gray-500">No lodgements scheduled today.</p>
          )}
          <ul className="space-y-1.5">
            {data.todays_lodgements?.map(m => (
              <li key={m.id} className="text-sm flex justify-between items-center">
                <span className="font-medium text-gray-800">{m.case__case_number ?? m.case_number}</span>
                <span className="text-gray-500 truncate max-w-[160px]">{m.case__title ?? m.case_title}</span>
              </li>
            ))}
          </ul>
        </DashboardCard>

        {/* 2. Upcoming Registrations */}
        <DashboardCard
          title="Upcoming Registrations (14 days)"
          icon="📋"
          color={CARD_STYLES.registrations}
          count={data.upcoming_registrations?.length ?? 0}
        >
          {data.upcoming_registrations?.length === 0 && (
            <p className="text-sm text-gray-500">No registrations expected in 14 days.</p>
          )}
          <ul className="space-y-1.5">
            {data.upcoming_registrations?.map(m => (
              <li key={m.id} className="text-sm flex justify-between items-center gap-2">
                <span className="font-medium text-gray-800">{m.case__case_number ?? m.case_number}</span>
                <div className="flex items-center gap-1.5">
                  <DaysInStageBadge updatedAt={m.case__updated_at} />
                  <span className="text-gray-500 text-xs">{m.expected_registration_date}</span>
                </div>
              </li>
            ))}
          </ul>
        </DashboardCard>

        {/* 3. BURS Outstanding */}
        <DashboardCard
          title="BURS Outstanding"
          icon="🏦"
          color={CARD_STYLES.burs}
          count={data.burs_outstanding?.length ?? 0}
        >
          {data.burs_outstanding?.length === 0 && (
            <p className="text-sm text-gray-500">No outstanding BURS submissions.</p>
          )}
          <ul className="space-y-1.5">
            {data.burs_outstanding?.map(m => (
              <li key={m.id} className="text-sm flex justify-between items-center gap-2">
                <span className="font-medium text-gray-800">{m.case__case_number ?? m.case_number}</span>
                <div className="flex items-center gap-1.5">
                  <DaysInStageBadge updatedAt={m.case__updated_at} />
                  <StatusBadge value="Pending" label="BURS Due" color="bg-yellow-100 text-yellow-800" />
                </div>
              </li>
            ))}
          </ul>
        </DashboardCard>

        {/* 4. Compliance Certs Outstanding */}
        <DashboardCard
          title="Compliance Certs Outstanding"
          icon="📄"
          color={CARD_STYLES.compliance}
          count={data.compliance_certs_outstanding?.length ?? 0}
        >
          {data.compliance_certs_outstanding?.length === 0 && (
            <p className="text-sm text-gray-500">No matters awaiting compliance certificates.</p>
          )}
          <ul className="space-y-1.5">
            {data.compliance_certs_outstanding?.map(m => (
              <li key={m.id} className="text-sm flex justify-between items-center gap-2">
                <span className="font-medium text-gray-800">{m.case__case_number ?? m.case_number}</span>
                <div className="flex items-center gap-1.5">
                  <DaysInStageBadge updatedAt={m.case__updated_at} />
                  <StatusBadge value="Pending" label="Cert Due" color="bg-amber-100 text-amber-800" />
                </div>
              </li>
            ))}
          </ul>
        </DashboardCard>

        {/* 5. FIA Chasers */}
        <DashboardCard
          title="FIA Chasers"
          icon="🆔"
          color={CARD_STYLES.fia}
          count={data.fia_chasers?.length ?? 0}
        >
          {data.fia_chasers?.length === 0 && (
            <p className="text-sm text-gray-500">All FIA/KYC verifications complete.</p>
          )}
          <ul className="space-y-1.5">
            {data.fia_chasers?.map(m => (
              <li key={m.id} className="text-sm flex justify-between">
                <span className="font-medium text-gray-800">{m.case__case_number ?? m.case_number}</span>
                <StatusBadge value="FIA Incomplete" label="Chase" color="bg-orange-100 text-orange-700" />
              </li>
            ))}
          </ul>
        </DashboardCard>

        {/* 6. Trust Balances */}
        <DashboardCard
          title="Trust Balances (Conveyancing)"
          icon="💰"
          color={CARD_STYLES.trust}
          count={data.trust_balances?.length ?? 0}
        >
          {data.trust_balances?.length === 0 && (
            <p className="text-sm text-gray-500">No trust accounts linked to conveyancing matters.</p>
          )}
          <ul className="space-y-1.5">
            {data.trust_balances?.map(acct => (
              <li key={acct.account_id ?? acct.id} className="text-sm flex justify-between items-center">
                <span className="text-gray-700 truncate max-w-[140px]">{acct.account_name}</span>
                <span className="font-semibold text-green-700">{formatCurrency(acct.balance)}</span>
              </li>
            ))}
          </ul>
        </DashboardCard>

        {/* Prep Room Deadlines */}
        <DashboardCard
          title="Prep Room Deadlines (≤5 days)"
          icon="⚠️"
          color={CARD_STYLES.prep}
          count={data.prep_room_deadlines?.length ?? 0}
        >
          {data.prep_room_deadlines?.length === 0 && (
            <p className="text-sm text-gray-500">No urgent examiner deadlines.</p>
          )}
          <ul className="space-y-2">
            {data.prep_room_deadlines?.map(note => (
              <li key={note.id} className="text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-800 truncate max-w-[160px]">{note.query_text?.slice(0, 60)}</span>
                  <StatusBadge
                    value={note.deadline_date}
                    label={note.deadline_date}
                    color="bg-yellow-100 text-yellow-700"
                  />
                </div>
              </li>
            ))}
          </ul>
        </DashboardCard>

      </div>
    </div>
  );
}
